import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useDevice } from '@/store/device';
import { useMirror, sanitizeSize } from '@/store/mirror';
import { MirrorCanvas, type MirrorCanvasHandle, type MirrorStatus } from './MirrorCanvas';
import { FpsBadge } from '@/components/perf/FpsBadge';
import { MirrorDragHeader, MirrorSideTools } from './FloatingToolbar';
import { MiniLogcat } from './MiniLogcat';
import { ClipboardHistoryPanel } from '@/components/clipboard/ClipboardHistoryPanel';
import { useClipboardActions } from '@/lib/clipboard/store';
import { useHostClipboardPoll } from '@/lib/clipboard/useHostClipboardPoll';
import {
  chooseWireMode,
  pushTextToDevice,
} from '@/lib/clipboard/pushTextToDevice';
import { adbApi, filesApi, transferApi } from '@/lib/api';
import {
  KEYCODE,
  KEY_ACTION,
  packBackOrScreenOn,
  packGetClipboard,
  packInjectKeycode,
} from '@/lib/scrcpy/control';
import {
  ArrowLeft,
  Circle as HomeIcon,
  Square as RecentsIcon,
} from 'lucide-react';

const LOGCAT_HEIGHT_FRACTION = 0.35;

/**
 * Top-level floating mirror window. Mounted once at the App root so route
 * changes do NOT remount it — the scrcpy WebSocket and the WebCodecs
 * decoder remain alive while the user navigates between pages.
 *
 * Rendered via `createPortal` to `document.body` so the window's stacking
 * context is independent of the main scroll container; combined with the
 * imperative `wheel` handler in `MirrorCanvas`, this fixes the "页面滚动
 * canvas 跟着抖" bug from earlier embedded views.
 *
 * Visibility model:
 *
 *   - **visible** (default): a draggable, resizable floating window at
 *     `pos` / `size`.
 *   - **hidden** (`minimized === true`): the wrapper is parked far off-
 *     screen with `pointer-events: none`; the canvas keeps decoding so
 *     re-opening from the sidebar restores the picture instantly.
 *
 * There is exactly ONE `<MirrorCanvas>` instance and it lives at a stable
 * position in the React tree across both states. We only flip the outer
 * wrapper's `left/top/pointer-events` — never the canvas's tree position.
 * This is what makes "hide → unhide" zero-reconnect.
 */
export function FloatingMirror(): JSX.Element | null {
  const isOpen = useMirror((s) => s.isOpen);
  const serial = useMirror((s) => s.serial);
  const minimized = useMirror((s) => s.minimized);
  const isFullscreen = useMirror((s) => s.isFullscreen);
  const pos = useMirror((s) => s.pos);
  const size = useMirror((s) => s.size);
  const showLogcat = useMirror((s) => s.showLogcat);
  const quality = useMirror((s) => s.quality);
  const readOnly = useMirror((s) => s.readOnly);
  const hudVisible = useMirror((s) => s.hudVisible);
  const videoBitRateKbps = useMirror((s) => s.videoBitRateKbps);
  const stayAwake = useMirror((s) => s.stayAwake);
  const lockOrientation = useMirror((s) => s.lockOrientation);
  const pinned = useMirror((s) => s.pinned);
  const setPos = useMirror((s) => s.setPos);
  const setSize = useMirror((s) => s.setSize);
  const setFullscreen = useMirror((s) => s.setFullscreen);
  // Phase 6 Document PiP. When non-null, the mirror is rendered into a
  // standalone OS-level window via React portal — the user can drag it
  // outside the browser, pin it on top of other apps, etc. Browsers
  // without `documentPictureInPicture` fall back to `pinned` z-index
  // (see store).
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const supportsDocPip =
    typeof window !== 'undefined' && 'documentPictureInPicture' in window;

  const isOnline = useDevice((s) => s.isOnline);

  const canvasRef = useRef<MirrorCanvasHandle>(null);
  /**
   * Direct DOM ref to the canvas wrapper div (the rounded-border element
   * that sits inside the phone-frame column). We measure its actual
   * width/height at runtime to figure out how much "chrome" (drag header,
   * side rail, padding, …) the mirror window has, so the aspect-lock
   * algorithm doesn't depend on hardcoded pixel constants — any future
   * padding / divider / row tweak is automatically reflected.
   */
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const sideToolsWidth = useMirror((s) => s.sideToolsWidth);
  const [status, setStatus] = useState<MirrorStatus>({ kind: 'connecting' });
  // Phase 4 drag-and-drop UI state. The `dragDepth` counter is the
  // canonical fix for the "drop zone keeps flickering when the cursor
  // moves across nested children" problem — every child re-fires
  // `dragenter` / `dragleave`, but the counter only flips back to 0 when
  // the cursor truly leaves the whole wrapper.
  const dragDepthRef = useRef(0);
  const [dragHint, setDragHint] = useState<{
    active: boolean;
    pendingCount: number;
    lastMsg: string | null;
  }>({ active: false, pendingCount: 0, lastMsg: null });

  /* ----------------- Phase 4 drag handlers ----------------- */
  const flashDropMsg = useCallback((msg: string): void => {
    setDragHint((cur) => ({ ...cur, lastMsg: msg }));
    window.setTimeout(
      () =>
        setDragHint((cur) => (cur.lastMsg === msg ? { ...cur, lastMsg: null } : cur)),
      2400,
    );
  }, []);

  /**
   * Decide whether `dataTransfer` contains anything we care about. We're
   * deliberately strict in `dragenter` to avoid the drop zone hijacking
   * unrelated drag operations (e.g. the user dragging tabs around).
   */
  const hasDroppablePayload = (dt: DataTransfer): boolean => {
    if (dt.types.includes('Files')) return true;
    if (dt.types.includes('text/plain')) return true;
    return false;
  };

  const onDragEnter = useCallback((e: React.DragEvent): void => {
    if (!hasDroppablePayload(e.dataTransfer)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragHint((cur) => (cur.active ? cur : { ...cur, active: true }));
  }, []);

  const onDragOver = useCallback((e: React.DragEvent): void => {
    if (!hasDroppablePayload(e.dataTransfer)) return;
    e.preventDefault();
    // Hint to the OS that we'll copy on drop. Without this, file drags
    // from Finder/Explorer show a "no entry" cursor and the drop event
    // never fires.
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent): void => {
    if (!hasDroppablePayload(e.dataTransfer)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setDragHint((cur) => ({ ...cur, active: false }));
    }
  }, []);

  const isApk = (f: File): boolean =>
    /\.apk$/i.test(f.name) || f.type === 'application/vnd.android.package-archive';
  // .xapk drops are delegated to the global InstallDropZone (apk too,
  // but we keep the in-mirror apk path as a backup since the global
  // overlay only fires when the drop happens on the SPA viewport).
  const isXapk = (f: File): boolean => /\.xapk$/i.test(f.name);

  /* ----------------- clipboard history (B2 prefs) ----------------- */
  // The store wrapper is a no-op when serial is null, so it's safe to
  // call here regardless of mirror state. The host-clipboard poll runs
  // ONLY while a serial is present + the browser is focused — see
  // `useHostClipboardPoll` for the full guard logic.
  const clipboardActions = useClipboardActions(serial);
  useHostClipboardPoll(serial);
  const [clipHistoryOpen, setClipHistoryOpen] = useState(false);

  const sendText = useCallback((text: string): void => {
    const handle = canvasRef.current;
    if (!handle) return;
    // Cmd+V 快捷键、drag-drop fallback —— 用户视角是"快速 paste 一段
    // 主机端文本"，不一定希望污染设备剪贴板。保持默认分发：短 ASCII
    // 走 INJECT_TEXT（直接 keystroke 注入，不动设备剪贴板），其余走
    // SET_CLIPBOARD paste=true。
    pushTextToDevice((bytes) => handle.sendControl(bytes), text);
  }, []);

  /**
   * "复制+自动粘贴"语义 —— 给抽屉卡片的「→ 复制到手机」和 Composer
   * 「保存并复制到手机」用。和 `sendText` 的差别：强制走 SET_CLIPBOARD
   * paste=true 路径，连短 ASCII 也不再走 INJECT_TEXT。原因：按钮文案
   * 明确写了"复制"，用户期望设备剪贴板里能看到这条文本（之后长按
   * 粘贴依然有效）；INJECT_TEXT 只注入按键、不动剪贴板，会让用户疑惑
   * "我刚才复制到手机的怎么剪贴板里没有"。
   */
  const copyAndPasteToDevice = useCallback((text: string): void => {
    const handle = canvasRef.current;
    if (!handle) return;
    pushTextToDevice((bytes) => handle.sendControl(bytes), text, {
      forceClipboard: true,
    });
  }, []);

  const onDrop = useCallback(
    async (e: React.DragEvent): Promise<void> => {
      if (!hasDroppablePayload(e.dataTransfer)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setDragHint((cur) => ({ ...cur, active: false }));
      if (!serial) return;

      const droppedFiles = Array.from(e.dataTransfer.files);
      const text = e.dataTransfer.getData('text/plain');

      // Text-only drop (no files): inject straight away. We deliberately
      // skip text injection when there are also files — Finder/Explorer
      // often attach a `text/plain` synth path next to the file list, and
      // injecting it would dump an OS path into the active field on top
      // of installing the APK.
      if (droppedFiles.length === 0 && text) {
        sendText(text);
        const useClipboardPath = chooseWireMode(text) === 'clipboard-paste';
        flashDropMsg(
          useClipboardPath
            ? '已通过剪贴板粘贴（非 ASCII 或长文本）'
            : `已输入：${text.length > 24 ? text.slice(0, 24) + '…' : text}`,
        );
        return;
      }
      if (droppedFiles.length === 0) return;

      // .apk / .xapk drops are owned by the global InstallDropZone — it
      // listens on `window` in the capture phase so a drop anywhere on
      // the SPA installs onto the active device with the same plumbing
      // (upload → app.install / app.installXapk). We filter those out
      // here so the mirror only sees genuine file pushes.
      const filesToPush = droppedFiles.filter((f) => !isApk(f) && !isXapk(f));
      if (filesToPush.length === 0) return;

      setDragHint((cur) => ({ ...cur, pendingCount: cur.pendingCount + filesToPush.length }));
      // Process sequentially so the user sees ordered toast messages and
      // doesn't slam adb with N concurrent pushes.
      for (const file of filesToPush) {
        try {
          // Stream the bytes through transfer.push so the server
          // doesn't keep a temp copy on disk. Lands in the device's
          // Download folder by convention.
          flashDropMsg(`正在推送 ${file.name}…`);
          await transferApi.push(serial, `/sdcard/Download/${file.name}`, file);
          flashDropMsg(`已推送到 /sdcard/Download/${file.name}`);
        } catch (err) {
          flashDropMsg(`${file.name} 失败：${(err as Error).message}`);
        } finally {
          setDragHint((cur) => ({
            ...cur,
            pendingCount: Math.max(0, cur.pendingCount - 1),
          }));
        }
      }
    },
    [serial, sendText, flashDropMsg],
  );

  /* ----------------- Phase 5 clipboard plumbing ----------------- */
  const [clipboardToast, setClipboardToast] = useState<string | null>(null);
  const flashClipboard = useCallback((msg: string): void => {
    setClipboardToast(msg);
    window.setTimeout(
      () => setClipboardToast((cur) => (cur === msg ? null : cur)),
      2400,
    );
  }, []);

  const onDeviceClipboard = useCallback(
    (text: string): void => {
      // Log the device-side copy into the per-serial history bucket.
      // The clipboard panel's right column is the canonical record for
      // device clipboard activity. We deliberately do *not* mirror the
      // text into the host system clipboard here: that round-trip used
      // to confuse the host clipboard poll, which would read its own
      // recently-written value back and re-record it as a `host` entry,
      // making device strings "leak" into the PC column. Users who
      // want a device entry on their PC clipboard click the explicit
      // `← 复制到电脑` action in the panel.
      if (!text) return;
      clipboardActions.push({ text, source: 'device' });
      const preview = text.length > 30 ? text.slice(0, 30) + '…' : text;
      flashClipboard(
        `已收到设备剪贴板：${preview}（点 ← 复制到电脑 可同步到 PC）`,
      );
    },
    [flashClipboard, clipboardActions],
  );

  /**
   * Inject a single Android keycode (down + up) onto the device. Shared
   * between the bottom nav-bar overlay and the right-edge hardware
   * button overlay so each surface stays a thin presentational wrapper.
   */
  const tapKey = useCallback((keycode: number): void => {
    const handle = canvasRef.current;
    if (!handle) return;
    handle.sendControl(
      packInjectKeycode({ action: KEY_ACTION.DOWN, keycode, repeat: 0, metaState: 0 }),
    );
    handle.sendControl(
      packInjectKeycode({ action: KEY_ACTION.UP, keycode, repeat: 0, metaState: 0 }),
    );
  }, []);

  const tapBack = useCallback((): void => {
    const handle = canvasRef.current;
    if (!handle) return;
    // BACK is special: scrcpy provides BackOrScreenOn which both injects
    // BACK *and* wakes the display if it's currently off — strictly
    // better than a raw KEYCODE_BACK keycode.
    handle.sendControl(packBackOrScreenOn(KEY_ACTION.UP));
  }, []);

  const pullClipboardFromDevice = useCallback((): void => {
    const handle = canvasRef.current;
    if (!handle) return;
    // copy_key=0 (NONE) — just read the current device clipboard. 1=COPY
    // would synthesize a COPY key first; users almost never want that.
    handle.sendControl(packGetClipboard(0));
    flashClipboard('已请求设备剪贴板…');
  }, [flashClipboard]);

  const pasteClipboardToDevice = useCallback(async (): Promise<void> => {
    const handle = canvasRef.current;
    if (!handle) return;
    try {
      const w = window as typeof window & { navigator?: Navigator };
      const reader = w.navigator?.clipboard?.readText?.bind(w.navigator.clipboard);
      if (!reader) {
        flashClipboard('浏览器不支持读取剪贴板');
        return;
      }
      const text = await reader();
      if (!text) {
        flashClipboard('剪贴板为空');
        return;
      }
      // ASCII / 长度的分发交给 helper —— 这是 toolbar 快捷动作、抽屉里
      // 「→ 复制到手机」、Cmd+V 快捷键、拖放 fallback 共享的同一份逻辑。
      const result = pushTextToDevice((bytes) => handle.sendControl(bytes), text);
      if (result.mode === 'noop') return;
      // 顺便把这条主机端文本写进当前设备的历史，让用户在抽屉里也能
      // 看到自己刚 paste 过的内容（source = 'host'）。
      clipboardActions.push({ text, source: 'host' });
      const usedClipboard = result.mode === 'clipboard-paste';
      flashClipboard(
        `已粘贴到设备（${result.chars} 字符${usedClipboard ? '，含非 ASCII 走剪贴板粘贴' : ''}）`,
      );
    } catch (err) {
      flashClipboard(`粘贴失败：${(err as Error).message}`);
    }
  }, [flashClipboard, clipboardActions]);

  /* ----------------- ESC exits fullscreen ----------------- */
  // We don't use the browser's native `Fullscreen API` here because that
  // ties us to a single element AND because the mirror is in a portal —
  // toggling a CSS-driven "viewport fill" is simpler, keeps the canvas
  // in the same React tree, and survives PiP / spectator transitions.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen, setFullscreen]);

  /* ----------------- Document PiP plumbing ----------------- */
  /**
   * Clone the SPA's stylesheets into the PiP window so Tailwind utility
   * classes resolve identically. External `<link href>` sheets are
   * re-linked (cheap, browser deduplicates), inline `<style>` blobs are
   * copied as cssText. Cross-origin sheets that throw on `cssRules`
   * are silently skipped — `tailwind.css` is same-origin so they only
   * apply to e.g. third-party widgets we don't ship.
   */
  const copyStylesToPipDocument = (src: Document, dst: Document): void => {
    Array.from(src.styleSheets).forEach((sheet) => {
      try {
        if (sheet.href) {
          const link = dst.createElement('link');
          link.rel = 'stylesheet';
          link.href = sheet.href;
          dst.head.appendChild(link);
        } else {
          const css = Array.from(sheet.cssRules)
            .map((rule) => rule.cssText)
            .join('\n');
          const style = dst.createElement('style');
          style.appendChild(dst.createTextNode(css));
          dst.head.appendChild(style);
        }
      } catch {
        /* cross-origin sheet — skip */
      }
    });
    // Inherit the SPA's background color so the PiP window doesn't
    // briefly flash white when it first appears.
    dst.body.style.margin = '0';
    dst.body.style.padding = '0';
    dst.body.style.overflow = 'hidden';
    dst.body.style.background = getComputedStyle(src.body).backgroundColor || '#000';
  };

  const togglePip = useCallback(async (): Promise<void> => {
    if (!supportsDocPip) return;
    if (pipWindow) {
      pipWindow.close();
      setPipWindow(null);
      return;
    }
    try {
      const docPip = (window as unknown as {
        documentPictureInPicture: {
          requestWindow: (opts?: { width?: number; height?: number }) => Promise<Window>;
        };
      }).documentPictureInPicture;
      const pip = await docPip.requestWindow({ width: size.w, height: size.h });
      copyStylesToPipDocument(document, pip.document);
      // Browsers fire 'pagehide' when the user closes the PiP window
      // (X button, or another `requestWindow` claim). We listen so the
      // toolbar can flip back to "enter PiP" mode automatically.
      pip.addEventListener('pagehide', () => setPipWindow(null), { once: true });
      setPipWindow(pip);
    } catch (err) {
      flashClipboard(`脱离窗口失败：${(err as Error).message}`);
    }
  }, [supportsDocPip, pipWindow, size.w, size.h, flashClipboard]);

  // Tear down any active PiP window when the mirror itself closes.
  useEffect(() => {
    if (isOpen && serial) return;
    if (pipWindow) {
      pipWindow.close();
      setPipWindow(null);
    }
  }, [isOpen, serial, pipWindow]);

  /* ----------------- Ctrl/Cmd + C/V clipboard shortcuts ----------------- */
  // Only react when the floating mirror is visible AND focus is somewhere
  // inside our wrapper (typically the canvas after a click). This avoids
  // hijacking native Ctrl+C of a code editor mounted elsewhere in the SPA.
  const outerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen || minimized || readOnly) return;
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k !== 'c' && k !== 'v') return;
      const focus = document.activeElement as HTMLElement | null;
      if (!focus || !outerRef.current?.contains(focus)) return;
      // Bail when focus is in a real text-entry widget — let the browser
      // handle copy/paste normally there.
      const tag = focus.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || focus.isContentEditable) return;
      e.preventDefault();
      if (k === 'c') pullClipboardFromDevice();
      else void pasteClipboardToDevice();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, minimized, readOnly, pullClipboardFromDevice, pasteClipboardToDevice]);

  /* ----------------- aspect-lock helpers ----------------- */
  /**
   * Read the current canvas wrapper's drawn size and derive how much
   * "chrome" (drag header, side rail, bottom nav, paddings, …) the
   * window currently has. Returns null when the wrapper hasn't been
   * laid out yet (e.g. during the very first render or while the
   * window is parked off-screen via `minimized`). Always non-negative
   * because the wrapper is always smaller than the outer window.
   */
  const measureChrome = useCallback((): { w: number; h: number } | null => {
    const wrapper = canvasWrapRef.current;
    if (!wrapper) return null;
    const rect = wrapper.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const sz = useMirror.getState().size;
    return {
      w: Math.max(0, sz.w - rect.width),
      h: Math.max(0, sz.h - rect.height),
    };
  }, []);

  /**
   * Compute a window size that keeps the canvas wrapper at the device's
   * native aspect ratio. The math:
   *
   *   canvasW / canvasH === aspect      (lock)
   *   windowW = canvasW + chromeW       (chrome stays a fixed offset)
   *   windowH = canvasH + chromeH
   *
   * `preferredCanvasH`:
   *   - undefined → "fit_screen": maximize canvas inside viewport (this
   *     is what aspect-change / viewport-resize / `贴合屏幕` triggers)
   *   - number    → use it as the desired canvas height, but clamp by
   *     the viewport-derived ceiling (this is what corner-drag and the
   *     "side rail width changed" effect use to avoid exploding the
   *     window when the user only meant to nudge it)
   *
   * Output is fed through `sanitizeSize` so MIN_W/MIN_H/MAX_W/MAX_H
   * still apply — at extreme viewport sizes the lock can be slightly
   * violated, but the user gets a usable window instead of NaN.
   */
  const aspectLockedSize = useCallback(
    (args: {
      aspect: number;
      chromeW: number;
      chromeH: number;
      preferredCanvasH?: number;
      viewportW: number;
      viewportH: number;
    }): { w: number; h: number } => {
      const margin = 24;
      const maxCanvasW = Math.max(1, args.viewportW - margin - args.chromeW);
      const maxCanvasH = Math.max(1, args.viewportH - margin - args.chromeH);
      // Canvas is constrained by both axes; the actual height ceiling is
      // whichever bound bites first when we project through the aspect.
      const cap = Math.min(maxCanvasH, maxCanvasW / args.aspect);
      const ch =
        args.preferredCanvasH != null
          ? Math.min(cap, Math.max(1, args.preferredCanvasH))
          : cap;
      const cw = ch * args.aspect;
      return sanitizeSize({
        w: Math.round(cw + args.chromeW),
        h: Math.round(ch + args.chromeH),
      });
    },
    [],
  );

  /* ----------------- aspect-lock auto-reflow ----------------- */
  /**
   * Trigger key for "device dimensions changed" — derived from the live
   * meta. Stays empty string while connecting / errored, so unrelated
   * status flips (e.g. perf updates) don't stomp on the user's manual
   * size, but rotation (w↔h) and the very first live frame do reflow.
   */
  const aspectKey =
    status.kind === 'live' ? `${status.meta.w}x${status.meta.h}` : '';

  // (i) aspect changed (device rotation / first live frame) → fit_screen
  useEffect(() => {
    if (status.kind !== 'live') return;
    if (minimized || isFullscreen || pipWindow != null) return;
    const aspect = status.meta.w / status.meta.h;
    if (!Number.isFinite(aspect) || aspect <= 0) return;
    // Defer to the next paint so the canvas wrapper has a chance to
    // settle into its new layout (the drag header / side rail width
    // may still be laying out on the very first 'live' tick).
    let raf = requestAnimationFrame(() => {
      const ch = measureChrome();
      if (!ch) return;
      setSize(
        aspectLockedSize({
          aspect,
          chromeW: ch.w,
          chromeH: ch.h,
          viewportW: window.innerWidth,
          viewportH: window.innerHeight,
        }),
      );
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspectKey]);

  // (ii) side rail width changed → preserve canvas height, just reshape
  //      the window. Skip during fullscreen / PiP / minimized for the
  //      same reason as (i).
  useEffect(() => {
    if (status.kind !== 'live') return;
    if (minimized || isFullscreen || pipWindow != null) return;
    const aspect = status.meta.w / status.meta.h;
    if (!Number.isFinite(aspect) || aspect <= 0) return;
    const raf = requestAnimationFrame(() => {
      const ch = measureChrome();
      if (!ch) return;
      const curCanvasH = useMirror.getState().size.h - ch.h;
      setSize(
        aspectLockedSize({
          aspect,
          chromeW: ch.w,
          chromeH: ch.h,
          preferredCanvasH: curCanvasH,
          viewportW: window.innerWidth,
          viewportH: window.innerHeight,
        }),
      );
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideToolsWidth]);

  // (iii) viewport resize → pull the window back inside the available
  //       area. mirror.ts already clamps `pos`, but the size also needs
  //       to shrink when the user makes the browser smaller than the
  //       current floating window. Debounced via rAF so a continuous
  //       drag-resize doesn't burn 60+ writes/sec.
  useEffect(() => {
    if (status.kind !== 'live') return;
    if (minimized || isFullscreen || pipWindow != null) return;
    let raf: number | null = null;
    const onResize = (): void => {
      if (raf != null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        if (status.kind !== 'live') return;
        if (minimized || isFullscreen || pipWindow != null) return;
        const aspect = status.meta.w / status.meta.h;
        if (!Number.isFinite(aspect) || aspect <= 0) return;
        const ch = measureChrome();
        if (!ch) return;
        const curCanvasH = useMirror.getState().size.h - ch.h;
        setSize(
          aspectLockedSize({
            aspect,
            chromeW: ch.w,
            chromeH: ch.h,
            preferredCanvasH: curCanvasH,
            viewportW: window.innerWidth,
            viewportH: window.innerHeight,
          }),
        );
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (raf != null) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspectKey, minimized, isFullscreen, pipWindow]);

  /* ----------------- drag (toolbar header) ----------------- */
  const onDragStart = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      const startPos = { ...useMirror.getState().pos };
      const startMouse = { x: e.clientX, y: e.clientY };
      const move = (ev: PointerEvent): void => {
        setPos({
          x: startPos.x + (ev.clientX - startMouse.x),
          y: startPos.y + (ev.clientY - startMouse.y),
        });
      };
      const up = (): void => {
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
        document.removeEventListener('pointercancel', up);
      };
      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
      document.addEventListener('pointercancel', up);
    },
    [setPos]
  );

  /* ----------------- resize (all 4 corners) ----------------- */
  /**
   * Per-corner resize with **aspect-lock**. While the device is `live`
   * we never let the canvas's width/height ratio drift away from
   * `device.w / device.h` — that is what eliminates the black bars
   * that 1:1-restore used to fight against. Algorithm:
   *
   *   1. Snapshot start size / pos / chrome at pointerdown.
   *   2. On move, compute the user's *raw* desired delta (dx, dy)
   *      and turn it into a desired window size per corner (the same
   *      sign rules as before).
   *   3. Pick the **dominant axis** — whichever axis the user moved
   *      proportionally more — as the source of truth for canvas
   *      height; the other axis is *derived* from the aspect lock.
   *      This makes diagonal drags feel intuitive: pulling SE mostly
   *      sideways grows width-driven, mostly downward grows
   *      height-driven, and a perfect 45° split picks one but lands
   *      on the same locked ratio either way.
   *   4. Re-derive `pos` so the OPPOSITE edge of the dragged corner
   *      stays nailed to its starting viewport coordinate (NW drag
   *      anchors SE corner, etc.).
   *
   * When the connection isn't live yet we fall back to free-resize so
   * the user can still drag the placeholder around without us
   * inventing a fake aspect.
   */
  const onResizeStart = useCallback(
    (dir: 'nw' | 'ne' | 'sw' | 'se') =>
      (e: React.PointerEvent) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        const startSize = { ...useMirror.getState().size };
        const startPos = { ...useMirror.getState().pos };
        const startMouse = { x: e.clientX, y: e.clientY };
        // Snapshot the current device aspect + chrome so a long drag
        // doesn't drift if a status update fires mid-gesture.
        const snapStatus = status;
        const liveAspect =
          snapStatus.kind === 'live' && snapStatus.meta.h > 0
            ? snapStatus.meta.w / snapStatus.meta.h
            : null;
        const startChrome = measureChrome();
        const move = (ev: PointerEvent): void => {
          const dx = ev.clientX - startMouse.x;
          const dy = ev.clientY - startMouse.y;
          // Desired raw window size before any clamping.
          const tgtW =
            dir === 'ne' || dir === 'se' ? startSize.w + dx : startSize.w - dx;
          const tgtH =
            dir === 'sw' || dir === 'se' ? startSize.h + dy : startSize.h - dy;

          let finalSize: { w: number; h: number };
          if (liveAspect != null && startChrome != null) {
            const startCanvasW = Math.max(1, startSize.w - startChrome.w);
            const startCanvasH = Math.max(1, startSize.h - startChrome.h);
            const tgtCanvasW = tgtW - startChrome.w;
            const tgtCanvasH = tgtH - startChrome.h;
            // Whichever axis the user moved proportionally more drives
            // the canvas height; the other is computed from the lock.
            const wRel = Math.abs(tgtCanvasW - startCanvasW) / startCanvasW;
            const hRel = Math.abs(tgtCanvasH - startCanvasH) / startCanvasH;
            const preferredCanvasH =
              wRel > hRel ? tgtCanvasW / liveAspect : tgtCanvasH;
            finalSize = aspectLockedSize({
              aspect: liveAspect,
              chromeW: startChrome.w,
              chromeH: startChrome.h,
              preferredCanvasH,
              viewportW: window.innerWidth,
              viewportH: window.innerHeight,
            });
          } else {
            finalSize = sanitizeSize({ w: tgtW, h: tgtH });
          }

          let nx = startPos.x;
          let ny = startPos.y;
          if (dir === 'nw' || dir === 'sw') {
            // Anchor the EAST edge: x must satisfy x + w = startX + startW
            nx = startPos.x + startSize.w - finalSize.w;
          }
          if (dir === 'nw' || dir === 'ne') {
            // Anchor the SOUTH edge: y must satisfy y + h = startY + startH
            ny = startPos.y + startSize.h - finalSize.h;
          }
          setSize(finalSize);
          if (dir === 'nw' || dir === 'sw' || dir === 'ne') {
            setPos({ x: nx, y: ny });
          }
        };
        const up = (): void => {
          document.removeEventListener('pointermove', move);
          document.removeEventListener('pointerup', up);
          document.removeEventListener('pointercancel', up);
        };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
        document.addEventListener('pointercancel', up);
      },
    [setSize, setPos, status, measureChrome, aspectLockedSize],
  );

  /* ----------------- portal target ----------------- */
  // When a PiP window is alive, render into its `<body>` instead of the
  // SPA's body — the mirror becomes a real OS-level window the user
  // can drag around the desktop. The decoded `<canvas>` and the WS
  // connection survive the move because React reparents the existing
  // DOM nodes.
  const portalTarget = pipWindow
    ? pipWindow.document.body
    : typeof document !== 'undefined'
      ? document.body
      : null;
  if (!portalTarget) return null;
  if (!isOpen || !serial) return null;

  const inPip = pipWindow != null;

  /* ----------------- outer wrapper geometry ----------------- */
  // Four visual states:
  //   - minimized   → park off-screen, pointer-events:none
  //   - inPip       → fill the PiP window's viewport (OS handles drag)
  //   - fullscreen  → fill the main viewport (100vw/100vh)
  //   - floating    → user-controlled `pos` + `size`
  const fillViewport = isFullscreen || inPip;
  const outerStyle: React.CSSProperties = minimized
    ? { left: -99999, top: 0, width: size.w, height: size.h }
    : fillViewport
      ? { left: 0, top: 0, width: '100vw', height: '100vh' }
      : { left: pos.x, top: pos.y, width: size.w, height: size.h };

  /* ----------------- per-overlay visibility ----------------- */
  // Spectator mode (readOnly) hides the toolbar entirely — without
  // control rights, the rotation / paste / quality buttons are all no-
  // ops on the device side, so showing them would mislead.
  const showToolbar = !minimized && !readOnly;
  const showLogcatPanel = showLogcat && !minimized;
  // Resize handle only in regular floating mode — fullscreen / PiP own
  // their whole window, dragging the SE corner doesn't make sense.
  const showResize = !minimized && !isFullscreen && !inPip;

  // The logcat reserves a slice of the wrapper height. When we fill the
  // viewport (fullscreen OR PiP) we don't have a numeric height to
  // multiply against, so we fall back to a `vh`-based slice that always
  // renders the same fraction regardless of viewport size.
  const logcatHeightStyle: React.CSSProperties['height'] = showLogcatPanel
    ? fillViewport
      ? `${Math.round(LOGCAT_HEIGHT_FRACTION * 100)}vh`
      : Math.round(size.h * LOGCAT_HEIGHT_FRACTION)
    : 0;

  return createPortal(
    <div
      className={clsx(
        'pointer-events-none fixed inset-0',
        // Three layers:
        //   - inPip          → z-0 (OS owns the window, in-DOM stacking
        //                     becomes irrelevant)
        //   - pinned         → z-[100] (above SPA modals at z-40 and
        //                     toasts at z-50; this is what the Pin
        //                     button is for — "stay on top of
        //                     everything inside the page")
        //   - default        → z-30 (below SPA modals/toasts so the
        //                     mirror doesn't accidentally cover a
        //                     confirmation dialog or transient toast,
        //                     which used to happen with the old z-[60])
        //
        // ClearDataModal still sits at z-[120] so it can cover even
        // the pinned floating window — destructive confirmations should
        // never be obscured.
        inPip ? 'z-0' : pinned ? 'z-[100]' : 'z-30'
      )}
      aria-hidden={minimized ? 'true' : undefined}
    >
      <div
        ref={outerRef}
        className={clsx(
          // Floating-card chrome with three layered effects so the
          // mirror reads as a discrete window above the SPA backdrop.
          // Earlier `ring-white/15` was effectively invisible because
          // it had no chroma against the dark slate-9xx backdrop. The
          // panel uses cyan (--accent), so we pick **violet** here:
          // a complementary cool/warm split (cyan ↔ violet) makes the
          // two floating layers easy to tell apart at a glance, and
          // doesn't collide with pinned (amber) or drag-active (accent)
          // states which already own their own colors.
          //
          //   - border-violet-400/45  ....... crisp 1px violet edge
          //   - ring-2 ring-violet-400/35  .. soft 2px violet glow
          //   - shadow stack  ............... long ambient + tight
          //                                   contact + 1px inset
          //                                   highlight for thickness
          //
          // drag-active / pinned states still take over and replace
          // both the default ring and the border (className order
          // makes them win).
          'absolute flex flex-col overflow-hidden bg-bg-card',
          'shadow-[0_32px_80px_rgba(0,0,0,0.7),0_6px_16px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.1)]',
          isFullscreen || inPip ? 'rounded-none' : 'rounded-lg',
          minimized ? 'pointer-events-none' : 'pointer-events-auto',
          dragHint.active
            ? 'border border-accent ring-2 ring-accent/70'
            : pinned && !inPip
              ? 'border border-amber-500/70 ring-1 ring-amber-500/40'
              : 'border border-violet-400/45 ring-2 ring-violet-400/35',
        )}
        style={outerStyle}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* ── 1) Top: drag header (device name + window controls + min/close) ── */}
        <div
          style={{ display: showToolbar ? undefined : 'none' }}
          className="shrink-0"
        >
          <MirrorDragHeader
            status={status}
            canvas={canvasRef}
            onDragStart={onDragStart}
            dragDisabled={isFullscreen || inPip}
            supportsDocPip={supportsDocPip}
            inPip={inPip}
            onTogglePip={togglePip}
          />
        </div>

        {/*
          ── 2) Main row: phone frame + vertical side tool rail ──────
          The phone frame mimics a real device's bezel: a soft rounded
          border around the canvas plus a fixed bottom "chrome" strip
          carrying the ◁ ○ □ nav buttons. The right rail stacks all
          actionable controls vertically (hw keys are intentionally at
          the top, where they'd be on a phone). Hidden in spectator
          mode (no control rights → no buttons make sense).
        */}
        <div className="flex min-h-0 flex-1">
          {/* Phone frame column: canvas + bottom nav chrome */}
          <div
            className="relative flex min-h-0 flex-1 flex-col p-1.5"
            style={{ backgroundColor: 'var(--bg-card, #1a1c20)' }}
          >
            {/*
              Inner canvas wrapper. The `border` + `rounded-xl` here is
              the "淡淡的边框" — gives a visible boundary to the device
              screen even when wallpaper colours nearly match the
              surrounding chrome. `overflow: hidden` clips the canvas
              to the rounded corners. `bg-black` ensures the letterbox
              strips (when the window's aspect ratio doesn't match the
              device's) read as device-side bezel rather than empty UI.
            */}
            <div
              ref={canvasWrapRef}
              className="relative flex-1 overflow-hidden rounded-xl border border-white/15 bg-black shadow-inner"
              style={{ minHeight: 0 }}
            >
              <MirrorCanvas
                ref={canvasRef}
                serial={serial}
                isOnline={isOnline}
                quality={quality}
                videoBitRateKbps={videoBitRateKbps}
                stayAwake={stayAwake}
                lockOrientation={lockOrientation}
                onStatusChange={setStatus}
                onDeviceClipboard={onDeviceClipboard}
                readOnly={readOnly}
              />
              {/* HUD overlay (FPS / kbps / latency) */}
              {hudVisible && status.kind === 'live' && status.perf && (
                <HudOverlay perf={status.perf} />
              )}
              {/*
                Device-side FPS badge (drives off `dumpsys gfxinfo` via the
                Performance page's perfSampler store). Independent from the
                HudOverlay above which shows the video pipeline FPS. Top-
                right so it doesn't collide with HudOverlay (top-left).
              */}
              {isOnline && (
                <FpsBadge
                  serial={serial}
                  variant="compact"
                  className="absolute right-2 top-2 z-10"
                />
              )}
              {/* Drag-and-drop overlay & toast */}
              {dragHint.active && (
                <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/45 text-center text-sm text-white">
                  <div className="rounded-lg border border-accent/70 px-4 py-2 backdrop-blur-sm">
                    <div className="font-medium">松开以安装 APK / 推送文件</div>
                    <div className="mt-0.5 text-[11px] text-white/75">
                      文本被注入到当前输入框；含中文 / 超长走剪贴板粘贴
                    </div>
                  </div>
                </div>
              )}
              {(dragHint.lastMsg || dragHint.pendingCount > 0) && !dragHint.active && (
                <div className="pointer-events-none absolute left-2 bottom-2 z-20 max-w-[90%] truncate rounded bg-black/70 px-2 py-1 text-[11px] text-white shadow">
                  {dragHint.pendingCount > 0 && (
                    <span className="mr-1 text-amber-300">[{dragHint.pendingCount}]</span>
                  )}
                  {dragHint.lastMsg ?? '正在处理…'}
                </div>
              )}
              {clipboardToast && (
                <div className="pointer-events-none absolute right-2 bottom-2 z-20 max-w-[80%] truncate rounded bg-black/70 px-2 py-1 text-[11px] text-white shadow">
                  {clipboardToast}
                </div>
              )}
            </div>
            {/*
              Bottom nav chrome — fixed-height strip BELOW the canvas
              (not overlaid on it) so it never covers the device's
              picture. Mimics the spot where on-screen nav buttons sit
              on a real Android device. Hidden in spectator mode.
            */}
            {!minimized && !readOnly && (
              <PhoneNavBar
                disabled={status.kind !== 'live'}
                onBack={tapBack}
                onHome={() => tapKey(KEYCODE.HOME)}
                onRecents={() => tapKey(KEYCODE.APP_SWITCH)}
              />
            )}
          </div>

          {/* Right rail: vertical side tools (hidden in spectator mode) */}
          {showToolbar && (
            <MirrorSideTools
              serial={serial}
              status={status}
              canvas={canvasRef}
              onOpenClipHistory={() => setClipHistoryOpen((v) => !v)}
              clipHistoryOpen={clipHistoryOpen}
            />
          )}
        </div>

        {/*
          The bottom MirrorConfigBar is gone — aspect-lock makes 1:1
          obsolete, quality / reconnect moved to the DragHeader, and
          rotate / orientation lock / stay-awake live inside the
          SideTools rail now (groups 方向 / 系统). The bitrate select
          is dropped entirely; the store still keeps `videoBitRateKbps`
          so the WS bridge can forward whatever the auto-bitrate path
          decides. Anything below this comment is unrelated logcat /
          drop-zone overlay UI.
        */}

        {/* ── Logcat panel ─────────────────────────────────────────── */}
        <div
          style={{
            display: showLogcatPanel ? undefined : 'none',
            height: logcatHeightStyle,
          }}
          className="shrink-0"
        >
          <MiniLogcat serial={serial} />
        </div>

        {/*
          ── Resize handles — one per corner ──────────────────────────
          The SE corner shows a visible 135° gradient indicator (the
          classic "you can grab this" affordance). The other three
          corners stay invisible 12×12 hit-targets — common pattern in
          OS-native window chrome where only one corner ships a glyph.
          NE sits a hair away from the close button (drag-header has
          `pr-3` to leave it ~8px of breathing room) so its hit area
          never overlaps the ✕.
        */}
        <ResizeCorner dir="nw" onPointerDown={onResizeStart('nw')} show={showResize} />
        <ResizeCorner dir="ne" onPointerDown={onResizeStart('ne')} show={showResize} />
        <ResizeCorner dir="sw" onPointerDown={onResizeStart('sw')} show={showResize} />
        <ResizeCorner
          dir="se"
          onPointerDown={onResizeStart('se')}
          show={showResize}
          glyph
        />
      </div>
      {/*
        ── Clipboard history drawer ────────────────────────────
        Lives outside the mirror's outer wrapper but inside the
        same portal — that way it owns its own stacking context
        and isn't clipped by the mirror's `overflow: hidden`. The
        drawer itself uses fixed positioning + z-110 so it sits
        above both unpinned and pinned mirror windows.
        Hidden in spectator (read-only) mode and while minimised.
      */}
      {!minimized && !readOnly && (
        <ClipboardHistoryPanel
          serial={serial}
          open={clipHistoryOpen}
          onClose={() => setClipHistoryOpen(false)}
          onPushTextToDevice={
            status.kind === 'live'
              ? (text) => copyAndPasteToDevice(text)
              : null
          }
        />
      )}
    </div>,
    portalTarget
  );
}

/**
 * Compact HUD pinned to the top-left of the canvas: rolling FPS, current
 * bandwidth, last-frame end-to-end latency. The values are computed in
 * `MirrorCanvas` (1s window) and piped via the `status.perf` field on the
 * live status. Designed not to steal pointer events so dragging /
 * clicking the canvas underneath still works.
 */
/**
 * Bottom phone-frame chrome strip carrying the on-screen nav buttons
 * (◁ back / ○ home / □ recents). Rendered BELOW the canvas (not on
 * top of it) so it never obscures any device pixel — the strip lives
 * inside the phone-frame column but outside the canvas wrapper. Sits
 * a fixed 28px tall, gentle backdrop tint so the buttons read as part
 * of the device's bezel rather than UI chrome.
 *
 * Hardware keys (vol+ / vol- / power) live in `MirrorSideTools` at the
 * top of the right rail — the geometry there matches a real phone's
 * side-button cluster while keeping the canvas unobstructed.
 */
function PhoneNavBar({
  disabled,
  onBack,
  onHome,
  onRecents,
}: {
  disabled: boolean;
  onBack: () => void;
  onHome: () => void;
  onRecents: () => void;
}): JSX.Element {
  return (
    <div className="mt-1.5 flex h-8 shrink-0 items-center justify-center gap-3 rounded-md bg-black/35">
      <NavBtn icon={ArrowLeft} label="返回" onClick={onBack} disabled={disabled} />
      <NavBtn icon={HomeIcon} label="主页" onClick={onHome} disabled={disabled} />
      <NavBtn icon={RecentsIcon} label="多任务" onClick={onRecents} disabled={disabled} />
    </div>
  );
}

/**
 * Window resize corner. 12×12 invisible hit area parked in one of the
 * four corners of the floating window. The SE corner additionally
 * shows a faint 135° gradient triangle so users can tell at a glance
 * the window IS resizable — the other three handles stay invisible
 * but discover themselves through the cursor change on hover.
 *
 * `z-30` keeps the corners above the canvas + side rail; they sit
 * BELOW modal overlays (z >= 100) so a "clear data" dialog can still
 * cover the entire mirror without leaking resize cursors at its edges.
 */
function ResizeCorner({
  dir,
  show,
  glyph,
  onPointerDown,
}: {
  dir: 'nw' | 'ne' | 'sw' | 'se';
  show: boolean;
  /** Render the visible SE-style triangle indicator. */
  glyph?: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
}): JSX.Element {
  const corner =
    dir === 'nw'
      ? 'left-0 top-0'
      : dir === 'ne'
        ? 'right-0 top-0'
        : dir === 'sw'
          ? 'left-0 bottom-0'
          : 'right-0 bottom-0';
  const cursor =
    dir === 'nw' || dir === 'se' ? 'cursor-nwse-resize' : 'cursor-nesw-resize';
  // The visible glyph points to the bottom-right by default. We rotate
  // it for whichever corner it lives in so it always points "outward"
  // from the window. Skipped entirely on the invisible corners.
  const glyphRotate =
    dir === 'nw'
      ? 'rotate(180deg)'
      : dir === 'ne'
        ? 'rotate(-90deg)'
        : dir === 'sw'
          ? 'rotate(90deg)'
          : 'rotate(0deg)';
  return (
    <div
      className={clsx('absolute z-30 h-3 w-3', corner, cursor)}
      onPointerDown={onPointerDown}
      title="拖动调整大小"
      style={{
        display: show ? undefined : 'none',
        background: glyph
          ? 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.35) 50%)'
          : undefined,
        transform: glyph ? glyphRotate : undefined,
      }}
    />
  );
}

function NavBtn({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      className={clsx(
        'flex h-6 w-9 items-center justify-center rounded text-white/85 transition-colors',
        disabled ? 'opacity-40' : 'hover:bg-white/15 active:bg-white/25',
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function HudOverlay({
  perf,
}: {
  perf: { fps: number; bitrateKbps: number; latencyMs: number | null };
}): JSX.Element {
  // Format kbps → Mbps once we cross 1000 so the readout doesn't grow
  // monstrously wide on high-bitrate sessions (16Mbps @ 1080p, etc).
  const bitrateLabel =
    perf.bitrateKbps >= 1000
      ? `${(perf.bitrateKbps / 1000).toFixed(1)} Mbps`
      : `${perf.bitrateKbps} kbps`;
  const latencyLabel =
    perf.latencyMs == null
      ? '— ms'
      : `${Math.max(0, Math.round(perf.latencyMs))} ms`;
  return (
    <div className="pointer-events-none absolute left-2 top-2 z-10 select-none rounded bg-black/55 px-2 py-1 font-mono text-[10px] leading-tight text-white shadow">
      <div>
        <span className="text-white/60">FPS</span> {perf.fps}
      </div>
      <div>
        <span className="text-white/60">码率</span> {bitrateLabel}
      </div>
      <div>
        <span className="text-white/60">延迟</span> {latencyLabel}
      </div>
    </div>
  );
}
